import { inject } from 'inversify';

import { ZOLL_DISPATCH, ZOLL_ONLINE, ZOLL_PRODUCTS } from '@/constants';
import { service } from '@/decorators/service';
import { Logger } from '@/services/Logger';
import { assignOwnProperties } from '@/utils/assignOwnProperties';
import { cleanObject } from '@/utils/clean';
import { fetchJson } from '@/utils/fetchJson';
import { pathJoin } from '@/utils/pathJoin';

export interface IEnvironment {
  // The base URL for AVL Modem (i.e. CDN).
  avlBaseUrl: string;
  // The base URL that this application is served from.
  baseUrl: string;
  error?: Error;
  filename: string;
  // The base URL for the SignalR websocket when in hybrid mode.
  hybridSignalrBaseUrl: string;
  isDev: boolean;
  isProd: boolean;
  isTest: boolean;
  name: string;
  // The base URL for the SignalR websocket when in standalone mode.
  signalrBaseUrl: string;
  version: string;
  // The base URL for the ZOLL Dispatch application.
  zdBaseUrl: string;
  // The base URL for the ZOLL Dispatch Admin application.
  zdAdminBaseUrl: string;
  // The base URL for the ZOLL Online Admin environment.
  zoAdminBaseUrl: string;
  // The "Admin - Dispatch" URL for the ZOLL Online Admin environment.
  zoAdminDispatchUrl: string;
  // The base URL for the ZOLL Online environment (i.e. layouts).
  zoBaseUrl: string;
  // The cookie domain used for authentication in the ZOLL Online environment.
  zoCookieDomain: string;
  // The login URL for the ZOLL Online environment.
  zoLoginUrl: string;
  // The Service Boundary base URL.
  zoServiceBoundaryBaseUrl: string;
  // The base URL for ZOLL Online static content (i.e. CDN).
  zoStaticBaseUrl: string;
  // The Service Zone base URL.
  zoServiceZoneBaseUrl: string;
  // The base URL for attachment service.
  zoAttachmentBaseUrl: string;
}

export type EnvironmentLike = object & Partial<IEnvironment> | IEnvironment | Environment;

export const defaults = <IEnvironment> {
  avlBaseUrl: ZOLL_ONLINE.AVL_BASE_URL,
  baseUrl: '',
  hybridSignalrBaseUrl: '',
  isDev: false,
  isProd: false,
  isTest: false,
  filename: '',
  name: 'local',
  signalrBaseUrl: '',
  version: PACKAGE_VERSION,
  zdBaseUrl: ZOLL_DISPATCH.BASE_URL,
  zdAdminBaseUrl: ZOLL_DISPATCH.ADMIN_BASE_URL,
  zoAdminBaseUrl: ZOLL_ONLINE.ADMIN_BASE_URL,
  zoAdminDispatchUrl: '',
  zoBaseUrl: ZOLL_ONLINE.BASE_URL,
  zoCookieDomain: ['', ...window.location.hostname.split('.').slice(1)].join('.'),
  zoLoginUrl: '',
  zoServiceBoundaryBaseUrl: ZOLL_ONLINE.SERVICE_BOUNDARY_BASE_URL,
  zoStaticBaseUrl: ZOLL_ONLINE.STATIC_BASE_URL,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  zoAttachmentBaseUrl: ZOLL_ONLINE.ZD_ATTACHMENT_BASE_URL,
};

// Provides local environment variables set during the build process.
export const local = () => <EnvironmentLike> cleanObject(<EnvironmentLike> {
  name: import.meta.env.VITE_ZD_ENV,
  avlBaseUrl: import.meta.env.VITE_AVL_BASE_URL,
  baseUrl: import.meta.env.VITE_ZD_BASE_URL,
  hybridSignalrBaseUrl: import.meta.env.VITE_ZD_HYBRID_SIGNALR_BASE_URL,
  signalrBaseUrl: import.meta.env.VITE_ZD_SIGNALR_BASE_URL,
  zoAdminBaseUrl: import.meta.env.VITE_ZD_ZO_ADMIN_BASE_URL,
  zoBaseUrl: import.meta.env.VITE_ZD_ZO_BASE_URL,
  zoServiceBoundaryBaseUrl: import.meta.env.VITE_ZD_ZO_SERVICE_BOUNDARY_BASE_URL,
  zoLoginUrl: import.meta.env.VITE_ZD_ZO_LOGIN_URL,
  zoStaticBaseUrl: import.meta.env.VITE_ZD_ZO_STATIC_BASE_URL,
  zoAttachmentBaseUrl: import.meta.env.VITE_ZD_ATTACHMENT_BASE_URL,
});

export const normalize = (environment?: EnvironmentLike): IEnvironment => {
  const normalized = <IEnvironment> {
    // Merge defaults to ensure all properties exist with default values.
    ...defaults,

    // Merge in cleaned passed values.
    ...cleanObject(environment),

    // Merge local environmental variables to override previous values.
    ...local(),

    // Ensure version set is based on the NodeJS application. This ultimately
    // becomes the ZO build version as it is replaced dynamically during deployment.
    version: PACKAGE_VERSION,
  };

  // Construct the ZO login URL from the base URL.
  if (!normalized.zoLoginUrl) {
    normalized.zoLoginUrl = pathJoin(normalized.zoBaseUrl, ZOLL_ONLINE.LOGIN_PATH);
  }

  // Construct the ZO Admin - Dispatch URL from the base admin URL.
  if (!normalized.zoAdminDispatchUrl) {
    let productKey = ZOLL_ONLINE.DISPATCH_PRODUCT_KEY;
    if (window.location.pathname.split('/')[1] === ZOLL_PRODUCTS.PORTAL) {
      productKey = ZOLL_ONLINE.PORTAL_PRODUCT_KEY;
    }

    normalized.zoAdminDispatchUrl = `${normalized.zoAdminBaseUrl}?productKey=${productKey}`;
  }

  // Ensure consistent environment names.
  normalized.name = normalized.name.toLowerCase();

  // Determine which environment it is.
  //
  // This deliberately checks the mode rather than `import.meta.env.DEV`. Vite sets
  // `DEV` to `!PROD`, so it is true for *every* non-production mode - test, staging
  // and any custom mode included. Using it here would force `isDev` and skip the
  // name-based classification below, leaving `isProd`/`isTest` permanently false
  // outside a production build.
  if (import.meta.env.MODE === 'development') {
    normalized.isDev = true;
  }
  else {
    switch (normalized.name) {
      case 'int':
      case 'pro':
      case 'prod':
      case 'production':
        normalized.isProd = true;
        break;

      case 'qa':
      case 'stage':
      case 'stg':
      case 'test':
        normalized.isTest = true;
        break;

      default:
        normalized.isDev = true;
    }
  }

  return normalized;
};

@service(Environment)
export class Environment implements IEnvironment {
  // Services.
  @inject(Logger) protected logger: Logger;

  // Properties.
  readonly avlBaseUrl: string;
  readonly baseUrl: string;
  readonly error?: Error;
  readonly filename: string;
  readonly hybridSignalrBaseUrl: string;
  readonly isDev: boolean;
  readonly isProd: boolean;
  readonly isTest: boolean;
  readonly name: string;
  readonly signalrBaseUrl: string;
  readonly version: string;
  readonly zdBaseUrl: string;
  readonly zdAdminBaseUrl: string;
  readonly zoAdminBaseUrl: string;
  readonly zoAdminDispatchUrl: string;
  readonly zoBaseUrl: string;
  readonly zoCookieDomain: string;
  readonly zoLoginUrl: string;
  readonly zoServiceBoundaryBaseUrl: string;
  readonly zoStaticBaseUrl: string;
  readonly zoServiceZoneBaseUrl: string;
  readonly zoAttachmentBaseUrl: string;

  private initialized: boolean;

  // Methods.
  async initialize(force = false) {
    if (!force && this.initialized) {
      return;
    }
    const { name } = local();

    // Prepend a specific environment file to load if one was provided.
    const filenames = ['environment.json'];
    if (name !== undefined) {
      filenames.unshift(`environment.${name}.json`);
    }

    let json: Partial<IEnvironment> | undefined;
    while (filenames.length) {
      const filename = filenames.shift();

      try {
        // Awaiting in a while loop is ok, but don't want to completely disable this rule.
        // eslint-disable-next-line no-await-in-loop
        json = await fetchJson<Partial<IEnvironment>>(`/${filename}`);
        if (json) {
          // Successful JSON data indicates previous errors can be ignored.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this.error as any) = undefined;
          json.filename = filename;
          break;
        }
      }
      catch (error) {
        this.logger.error(error);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.error as any) = error as Error;
        throw error;
      }
    }

    assignOwnProperties(this, normalize({ ...json }));

    this.initialized = true;
  }

  toJSON(): IEnvironment {
    return {
      avlBaseUrl: this.avlBaseUrl,
      baseUrl: this.baseUrl,
      error: this.error,
      filename: this.filename,
      hybridSignalrBaseUrl: this.hybridSignalrBaseUrl,
      isDev: this.isDev,
      isProd: this.isProd,
      isTest: this.isTest,
      name: this.name,
      signalrBaseUrl: this.signalrBaseUrl,
      version: this.version,
      zdBaseUrl: this.zdBaseUrl,
      zdAdminBaseUrl: this.zdAdminBaseUrl,
      zoAdminBaseUrl: this.zoAdminBaseUrl,
      zoAdminDispatchUrl: this.zoAdminDispatchUrl,
      zoBaseUrl: this.zoBaseUrl,
      zoCookieDomain: this.zoCookieDomain,
      zoLoginUrl: this.zoLoginUrl,
      zoServiceBoundaryBaseUrl: this.zoServiceBoundaryBaseUrl,
      zoStaticBaseUrl: this.zoStaticBaseUrl,
      zoServiceZoneBaseUrl: this.zoServiceZoneBaseUrl,
      zoAttachmentBaseUrl: this.zoAttachmentBaseUrl,
    };
  }
}
